import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth, User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged,
} from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject, Observable, filter } from 'rxjs';
import { PartnerAddress } from '../models/partner.model';

export interface RegisterPayload {
  displayName: string;
  companyName: string;
  phone: string;
  address: PartnerAddress;
  website: string;
  legalAcceptance?: {
    partnerAgreementAcceptedAt: string;
    partnerAgreementVersion: string;
    termsAcceptedAt: string;
  };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth   = inject(Auth);
  private fns    = inject(Functions);
  private router = inject(Router);

  // undefined = Firebase auth state not yet resolved (app initialising)
  // null      = resolved, nobody signed in
  // User      = resolved, user signed in
  private _user$ = new BehaviorSubject<User | null | undefined>(undefined);

  /**
   * Emits only after Firebase has resolved the initial session —
   * guards should use this so they never fire on a transient null.
   */
  readonly user$: Observable<User | null> = this._user$.pipe(
    filter((u): u is User | null => u !== undefined),
  );

  constructor() {
    onAuthStateChanged(this.auth, user => this._user$.next(user));
  }

  get currentUser(): User | null | undefined { return this._user$.value; }
  get isLoggedIn(): boolean { return !!this._user$.value; }

  /**
   * Register a new partner:
   *  1. Create Firebase Auth user
   *  2. Send email verification
   *  3. Call Cloud Function to create Firestore doc
   *
   * If steps 2–3 fail the Auth user is deleted so the partner can
   * retry without hitting "email already in use".
   */
  async register(
    email: string,
    password: string,
    profile: RegisterPayload,
  ): Promise<void> {
    const cred = await createUserWithEmailAndPassword(
      this.auth, email, password,
    );
    try {
      await sendEmailVerification(cred.user);
      const managePartner = httpsCallable(this.fns, 'managePartner');
      await managePartner({ action: 'register', ...profile, email });
      // Sign out immediately — the partner's application is pending review.
      // They must wait for admin approval before logging in.  Signing out
      // here also prevents guestGuard from redirecting "Back to Sign In"
      // straight to /dashboard, bypassing the approval gate.
      await signOut(this.auth);
    } catch (err) {
      // Roll back so the partner can retry cleanly without
      // "email already in use" on the next attempt.
      await cred.user.delete().catch(() => null);
      throw err;
    }
  }

  async login(email: string, password: string): Promise<void> {
    await signInWithEmailAndPassword(this.auth, email, password);
    await this.router.navigate(['/dashboard']);
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
    await this.router.navigate(['/login']);
  }

  async sendPasswordReset(email: string): Promise<void> {
    await sendPasswordResetEmail(this.auth, email);
  }
}
